import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

interface Device {
  id: string;
  venueId: string;
  venue?: { name: string };
  status: string;
  isAktivní: boolean;
  lastUsed: string | null;
  createdAt: string;
}

interface Provozovna {
  id: string;
  name: string;
}

const DeviceManagement = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [venues, setProvozovnas] = useState<Provozovna[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newDevice, setNewDevice] = useState({ venueId: '' });
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  const showMessage = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [venuesRes] = await Promise.all([
        api.get('/venues'),
      ]);

      const venuesData = venuesRes.data;
      setProvozovnas(venuesData);

      const allDevices: Device[] = [];
      venuesData.forEach((venue: any) => {
        if (venue.devices) {
          venue.devices.forEach((device: any) => {
            allDevices.push({
              ...device,
              venue: { name: venue.name },
            });
          });
        }
      });
      setDevices(allDevices);
    } catch (error) {
      console.error('Error fetching data:', error);
      showMessage('Načtení zařízení se nezdařilo', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateDevice = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/devices', newDevice);
      setCreatedApiKey(response.data.apiKey);
      showMessage('Zařízení bylo úspěšně vytvořeno!', 'success');
      setNewDevice({ venueId: '' });
      fetchData();
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Zařízení se nepodařilo vytvořit', 'error');
    }
  };

  const handleRegenerateKey = async (deviceId: string) => {
    if (!window.confirm('Opravdu chcete pokračovat? Původní API klíč se okamžitě deaktivuje.')) {
      return;
    }

    try {
      const response = await api.post(`/devices/${deviceId}/regenerate-key`);
      setCreatedApiKey(response.data.apiKey);
      showMessage('API klíč byl úspěšně obnoven!', 'success');
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Klíč se nepodařilo obnovit', 'error');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showMessage('Zkopírováno do schránky!', 'success');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'in-use':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Volné';
      case 'in-use':
        return 'Obsazené';
      case 'maintenance':
        return 'Servis';
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/admin"
              className="text-primary-600 hover:text-primary-700 font-semibold mb-2 inline-flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Zpět na přehled
            </Link>
            <h1 className="text-5xl font-black bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              🎮 Správa zařízení
            </h1>
          </div>
          <button
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setCreatedApiKey(null);
            }}
            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-2xl font-semibold hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300"
          >
            {showCreateForm ? 'Zrušit' : '+ Vytvořit zařízení'}
          </button>
        </div>

        {message && (
          <div className="mb-6 p-4 bg-white/80 backdrop-blur-lg rounded-2xl shadow-card text-center">
            <p className="text-gray-800 font-semibold">{message}</p>
          </div>
        )}

        {createdApiKey && (
          <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-3xl shadow-glow">
            <h3 className="text-xl font-bold text-green-800 mb-3">
              ⚠️ API klíč vytvořen – ihned si ho uložte!
            </h3>
            <p className="text-green-700 mb-4">
              Tento klíč se zobrazí pouze jednou. Hned si ho zkopírujte a bezpečně uložte.
            </p>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={createdApiKey}
                readOnly
                className="flex-1 px-4 py-3 border-2 border-green-300 rounded-2xl bg-white font-mono text-sm"
              />
              <button
                onClick={() => copyToClipboard(createdApiKey)}
                className="px-6 py-3 bg-green-600 text-white rounded-2xl font-semibold hover:bg-green-700 transition-all duration-300"
              >
                📋 Kopírovat
              </button>
            </div>
          </div>
        )}

        {showCreateForm && (
          <div className="mb-8 bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Vytvořit nové zařízení</h2>
            <form onSubmit={handleCreateDevice} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Provozovna</label>
                <select
                  value={newDevice.venueId}
                  onChange={(e) => setNewDevice({ ...newDevice, venueId: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-primary-500 focus:outline-none transition-all duration-300"
                  required
                >
                  <option value="">Vyberte provozovnu...</option>
                  {venues.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-2xl font-bold hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300"
              >
                Vytvořit zařízení a vygenerovat klíč
              </button>
            </form>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Všechna zařízení ({devices.length})</h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Načítám zařízení...</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-4">🎮</p>
              <p className="text-gray-600">Žádná zařízení nenalezena. Vytvořte první!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {devices.map((device) => (
                <div
                  key={device.id}
                  className="p-6 border-2 border-gray-200 rounded-2xl hover:border-primary-300 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-3">
                        <h3 className="text-xl font-bold text-gray-800">
                          ID zařízení: {device.id.slice(0, 6)}…
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(device.status)}`}>
                          {getStatusLabel(device.status)}
                        </span>
                        {device.isAktivní ? (
                          <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                            Aktivní
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                            Neaktivní
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Provozovna</p>
                          <p className="font-semibold text-gray-800">{device.venue?.name}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Poslední použití</p>
                          <p className="font-semibold text-gray-800">
                            {device.lastUsed
                              ? new Date(device.lastUsed).toLocaleDateString()
                              : 'Nikdy'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleRegenerateKey(device.id)}
                      className="ml-4 px-6 py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white rounded-2xl font-semibold hover:shadow-glow transition-all duration-300"
                    >
                      🔄 Obnovit klíč
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceManagement;
